// uploads — the rules server/routes/uploads.js actually enforces, in one place.
//
// Every multipart upload in the rebuild goes to that router, and it refuses on
// two grounds:
//
//   fileFilter   `!file.originalname.match(/\.(jpg|jpeg|png|gif|ico|svg)$/i)`
//                — the FILENAME EXTENSION, not the MIME type. A .webp labelled
//                  image/png is refused; a .png labelled application/octet-
//                  stream is accepted.
//   limits       `fileSize: 5 * 1024 * 1024`
//
// Both refusals arrive as multer errors, not as the router's own JSON, so what
// a person would otherwise see is "Only image files are allowed!" (which does
// not say which are) or a bare 500. Checking here first means the sentence
// names the real reason before anything is sent.
//
// This lived in apps/office/admin/Settings.js until the profile photo needed
// the same rules. It is here rather than there because two screens in two
// applications now mirror one server file, and a second copy is how the copies
// drift: the classic app already carries a THIRD version of this check
// (components/Profile/Profile.js tests `file.type.startsWith('image/')`, which
// accepts the .webp the server refuses and gives the worker a 500 instead of a
// sentence). Settings.js re-exports `rejectUpload` so its own suite, which
// imports it from there, keeps working.
//
// Deliberately NOT a MIME check. Mirroring the server means mirroring what it
// tests, wrong-looking or not; a client that is stricter or looser than the
// server refuses files that would work or promises files that will not.

/** The extensions server/routes/uploads.js `fileFilter` accepts, in its order. */
export const UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'ico', 'svg'];

/** `limits.fileSize` on the multer instance in that file. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * The multipart field name `POST /api/uploads` reads — `upload.single('file')`.
 * Any other key leaves `req.file` undefined and the handler answers 400
 * "No file uploaded", which reads like an empty picker rather than a bug.
 */
export const UPLOAD_FIELD = 'file';

/**
 * Why the server would refuse this file, as a sentence, or null.
 *
 * No file at all returns null: that is a cancelled picker, not an error.
 */
export function rejectUpload(file, { maxBytes = MAX_UPLOAD_BYTES } = {}) {
  if (!file) return null;
  const name = String(file.name || '');
  const ext = name.split('.').pop().toLowerCase();
  if (!name.includes('.') || !UPLOAD_EXTENSIONS.includes(ext)) {
    return `That file type is not accepted. Use ${UPLOAD_EXTENSIONS.join(', ')}.`;
  }
  if (file.size > maxBytes) {
    return `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${Math.round(maxBytes / 1024 / 1024)} MB.`;
  }
  return null;
}

/** The body `POST /api/uploads` expects for one file. */
export function uploadForm(file, field = UPLOAD_FIELD) {
  const form = new FormData();
  form.append(field, file);
  return form;
}
